import { useState, useCallback } from 'react';
import { useUI } from '../../contexts/UIProvider';
import { fetchGuideArticle } from './guide-slideshow-utils';
import GuideSlideshowPopup from './GuideSlideshowPopup';

export interface GuideIndexEntry {
  slug: string;
  title: string;
  description: string;
  thumbnail?: string;
}

interface GuideSelectionPopupProps {
  guides: GuideIndexEntry[];
  category: 'seller' | 'buyer';
}

const GuideSelectionPopup = ({ guides, category }: GuideSelectionPopupProps) => {
  const { setPopupContents, setPopupOpen } = useUI();
  const [loading, setLoading] = useState<string | null>(null);

  const close = useCallback(() => {
    setPopupOpen(false);
  }, [setPopupOpen]);

  const handleGuideClick = useCallback(async (slug: string) => {
    setLoading(slug);
    try {
      const guide = await fetchGuideArticle(category, slug);
      if (!guide) return;
      setPopupContents(<GuideSlideshowPopup guide={guide} />);
    } catch {
      // silently fail
    } finally {
      setLoading(null);
    }
  }, [category, setPopupContents]);

  const heading = category === 'seller' ? 'Seller Guides' : 'Buyer Guides';

  return (
    <div className="guide-slideshow flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-5 border-b border-gray-300 dark:border-neutral-700">
        <h2 className="text-[18px] font-semibold text-gray-900 dark:text-white">{heading}</h2>
        <button
          onClick={close}
          className="text-gray-500 hover:text-gray-900 dark:text-neutral-400 dark:hover:text-white transition-colors shrink-0 w-8 h-8 flex items-center justify-center"
          aria-label="Close guide menu"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* Guide list */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="guide-selection-list">
        {guides.map((guide) => (
          <button
            key={guide.slug}
            onClick={() => handleGuideClick(guide.slug)}
            disabled={loading === guide.slug}
            className="w-full text-left flex rounded-xl transition-colors disabled:opacity-60 overflow-hidden border bg-white hover:bg-gray-100 border-gray-300 hover:border-gray-400 dark:bg-neutral-800 dark:hover:bg-neutral-700 dark:border-neutral-700 dark:hover:border-neutral-600"
            data-testid={`guide-card-${guide.slug}`}
          >
            {guide.thumbnail && (
              <img
                src={guide.thumbnail}
                alt=""
                className="w-[72px] self-stretch object-cover shrink-0"
              />
            )}
            <div className="px-4 py-3 min-w-0">
              <h3 className="text-[15px] font-semibold mb-1 text-gray-900 dark:text-white">{guide.title}</h3>
              <p className="text-[13px] leading-snug text-gray-600 dark:text-neutral-400">{guide.description}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default GuideSelectionPopup;
